<template>
  <div class="w-100 d-flex flex-column justify-start align-start">
    <v-checkbox
      v-for="config in csvConfigs"
      :key="config.label"
      v-model="selectedItems"
      :label="config.label"
      :value="config"
      color="brandPrimary"
      hide-details
      class="mb-2"
      :disabled="exportInProgress"
    />
  </div>
  <DpplNewButton
    :disabled="buttonDisabled"
    color="brandPrimary"
    size="small"
    class="w-100 mt-4"
    @click="handleCsvDownload"
  >
    {{ buttonText }}
  </DpplNewButton>
</template>

<script setup>
import { ref, computed } from 'vue';
import DpplNewButton from '@/components/datapeople/DpplNewButton.vue';
import { useInsightsExport } from '@/stores/insights/export';
import { storeToRefs } from 'pinia';
import { useAppSnackbar } from '@/stores/app/snackbar';
import { wait } from '@/helpers/wait';
import { useTracking } from '@/stores/tracking/tracking';
import { useRoute } from 'vue-router';

const route = useRoute();
const emits = defineEmits(['close']);

const props = defineProps({
  exportConfig: {
    type: Object,
    required: true,
  },
});

const { exportInProgress } = storeToRefs(useInsightsExport());

const { updateExportInProgress } = useInsightsExport();
const {
  addErrorSnackbar,
  addSuccessSnackbar,
} = useAppSnackbar();

const selectedItems = ref([]);

const csvConfigs = computed(() => props.exportConfig.csvOptions || []);
const buttonDisabled = computed(() => exportInProgress.value || !selectedItems.value.length);

const buttonText = computed(() => {
  if (exportInProgress.value) return 'Downloading...';
  return `Download CSV${selectedItems.value.length > 1 ? 's' : ''}`;
});

const handleCsvDownload = async () => {
  const promises = selectedItems.value.map((config) => {
    if (config.csvDownloadFunction.constructor.name === 'AsyncFunction') {
      return config.csvDownloadFunction();
    }

    return new Promise((resolve) => {
      config.csvDownloadFunction();
      resolve(wait(config.waitTime || 2000)); // Simulate download time in case it takes a second
    });
  });

  try {
    updateExportInProgress(true);

    await Promise.all(promises);

    selectedItems.value.forEach((item) => {
      if (item.csvTrackingEventFn) {
        item.csvTrackingEventFn(item);
      } else {
        useTracking().insightsDownloadReport({
          reportName: route.name,
          downloadType: 'Individual Widgets',
          downloadLocation: 'Download Modal',
          fileType: 'CSV',
          widgetName: item.label,
        });
      }
    });

    addSuccessSnackbar({
      text: `CSV${selectedItems.value.length > 1 ? 's' : ''} downloaded successfully`,
    });
  } catch (error) {
    addErrorSnackbar({ text: 'Failed to download CSVs' });
    throw error;
  } finally {
    updateExportInProgress(false);
    emits('close');
  }
};
</script>

<style scoped lang="less">
:deep(.v-selection-control__input) {
  height: 20px;
  width: 20px;

  .v-icon {
    width: 20px;
    height: 20px;
  }

  input {
    width: 20px;
    height: 20px;
  }
}

:deep(.v-selection-control) {
  .v-selection-control__wrapper {
    height: 20px !important;
  }

  .v-label {
    font-size: 13px !important;
    font-weight: 500;
    line-height: 150%;
  }
}

:deep(.v-selection-control) {
  min-height: inherit !important;
}

:deep(.v-selection-control__input) > .v-icon {
  opacity: 1.0 !important;
}
</style>
